import { isPREligibleSet, prWeightOf, type PRRecord, type PRsByExercise } from "./pr.js";
import type { WorkoutSet } from "./schemas/workout.js";

// In-session PR comparison helper. Lives beside pr.ts (whose rep-max
// primitives it reuses) rather than inside it to keep that file under the
// 500-line budget.
//
// Fold already-performed sets from the IN-PROGRESS session into an
// existing PRsByExercise, returning a NEW map (the input is never
// mutated). Why this exists: the live logger checks a just-completed set
// against `prHistory`, which holds only PRIOR workouts. Two identical
// sets in the same session therefore both "beat" history and both flash
// the trophy — the 2nd 200×3 is not a new record, it's superseded by the
// 1st. Folding the session's earlier sets in fixes that: the 2nd set now
// sees the 1st in its rep buckets and the same strict-heavier-wins rule
// as prsFromHistory declines it. Ties never overwrite, so the EARLIEST
// set keeps the PR — the caller passes only the sets that precede the
// candidate. `setAt`/`workoutId` are blank: these records are transient,
// used for the in-session comparison only and never persisted.
export const foldSessionSetsIntoPRs = (
  base: PRsByExercise,
  exerciseId: string,
  sets: readonly WorkoutSet[],
): PRsByExercise => {
  let buckets: Record<number, PRRecord> | null = null;
  for (const set of sets) {
    if (!isPREligibleSet(set)) continue;
    if (buckets === null) buckets = { ...(base[exerciseId] ?? {}) };
    const setWeight = prWeightOf(set);
    for (let r = 1; r <= set.reps; r++) {
      const current = buckets[r];
      if (!current || setWeight > current.weightKg) {
        buckets[r] = { weightKg: setWeight, reps: set.reps, setAt: "", workoutId: "" };
      }
    }
  }
  if (buckets === null) return base;
  return { ...base, [exerciseId]: buckets };
};
